// Order Type options for PickupInfoModal. Each patch is complete (order_type +
// both MM flags) so switching never leaves a stale flag. `value` is distinct from
// order_type — receiving and mm_delivery both carry order_type 'delivery'.

const OPTION = (value, label, description, patch) => ({ value, label, description, patch });

// Always available; array order = dropdown order.
const BASE_OPTIONS = [
    OPTION('delivery', 'Delivery', 'Items are picked up from the origin and delivered to the customer.', {
        order_type: 'delivery',
        is_middle_mile: false,
        is_middle_mile_origin: false,
    }),
    OPTION('return', 'Return', 'Items are picked up from the customer and returned to the origin.', {
        order_type: 'return',
        is_middle_mile: false,
        is_middle_mile_origin: false,
    }),
    OPTION(
        'service',
        'Service Only',
        'Order has no products to transport — only on-site service work. Add services on the order after this step.',
        { order_type: 'service', is_middle_mile: false, is_middle_mile_origin: false }
    ),
    OPTION(
        'will_call',
        'Will Call',
        'Order will not have a delivery. The customer will pick up the order from the warehouse.',
        {
            order_type: 'will_call',
            is_middle_mile: false,
            is_middle_mile_origin: false,
        }
    ),
    OPTION('wh_inventory', 'Warehouse Inventory', 'Stock received into the warehouse with no customer delivery.', {
        order_type: 'wh_inventory',
        is_middle_mile: false,
        is_middle_mile_origin: false,
    }),
    OPTION('wh_return', 'Warehouse Return', 'Items returned from the warehouse back to the shipper.', {
        order_type: 'wh_return',
        is_middle_mile: false,
        is_middle_mile_origin: false,
    }),
    OPTION(
        'receiving',
        'Receiving + Delivery',
        "The shipper will coordinate the delivery of the items to the final mile agent's warehouse, then the items are delivered to the customer. Order will not have an origin or pickup location.",
        { order_type: 'delivery', is_middle_mile: true, is_middle_mile_origin: false }
    ),
    OPTION(
        'receiving_will_call',
        'Receiving + Will Call',
        "The shipper will coordinate the delivery of the items to the final mile agent's warehouse, then the customer picks up the order from the warehouse. Order will not have an origin or pickup location.",
        { order_type: 'will_call', is_middle_mile: true, is_middle_mile_origin: false }
    ),
];

// Gated on a MIDDLE_MILE pricing override.
const MIDDLE_MILE_OPTIONS = [
    OPTION(
        'mm_delivery',
        'Multi-Leg Delivery (MM + FM)',
        "A middle mile carrier will pickup from the origin address provided and deliver to the final mile agent's warehouse.",
        { order_type: 'delivery', is_middle_mile: true, is_middle_mile_origin: true }
    ),
    OPTION(
        'mm_return',
        'Multi-Leg Return',
        'A middle mile carrier returns the items from the final mile warehouse back to the origin.',
        {
            order_type: 'return',
            is_middle_mile: true,
            is_middle_mile_origin: true,
        }
    ),
];

const hasMiddleMileOverride = (pricingOverrides = []) =>
    pricingOverrides.some((override) => override.override_type === 'MIDDLE_MILE');

export const buildOrderTypeOptions = ({ pricingOverrides = [] } = {}) => {
    const options = [...BASE_OPTIONS];
    if (hasMiddleMileOverride(pricingOverrides)) {
        options.push(...MIDDLE_MILE_OPTIONS);
    }
    return options;
};

// Matches the full flag triple; unknown/exchange falls back to delivery.
export const resolveSelectedOptionValue = (pickupInfo = {}, options = []) => {
    const order_type = pickupInfo.order_type || 'delivery';
    const is_middle_mile = !!pickupInfo.is_middle_mile;
    const is_middle_mile_origin = !!pickupInfo.is_middle_mile_origin;

    const match = options.find(
        (option) =>
            option.patch.order_type === order_type &&
            !!option.patch.is_middle_mile === is_middle_mile &&
            !!option.patch.is_middle_mile_origin === is_middle_mile_origin
    );

    return match ? match.value : 'delivery';
};

export const getDefaultOptionValue = ({ circles } = {}) =>
    (circles || {})['import-default-receiving'] ? 'receiving' : 'delivery';

export const shouldApplyMiddleMileDefault = (pickupInfo = {}) =>
    pickupInfo.order_type === undefined && pickupInfo.is_middle_mile === undefined;

// Patch to seed on mount, or null when the order type is already set (store.js
// or user) so seeding never overrides it.
export const resolveDefaultSeedPatch = ({ pickupInfo = {}, circles = {}, options = [] } = {}) => {
    if (!shouldApplyMiddleMileDefault(pickupInfo)) return null;
    return options.find((o) => o.value === getDefaultOptionValue({ circles }))?.patch ?? null;
};
